// src/utils/schema.js
//
// Schema.org JSON-LD generators for the routes that need them (s62).
//
// The site already renders:
//   - Organization + WebSite + Person × 13 (in index.html, all pages)
//   - FAQPage (CityPage, StatePage)
//
// This module adds:
//   - WebApplication  (23 tool pages — s80)
//   - CollectionPage  (hubs / indexes — s80)
//   - WebPage         (static trust pages — s80)
//   - Article         (ArticlePage)
//   - BlogPosting     (BlogPost)
//   - Article + TechArticle (GuidePage — methodology guides are
//                          technical articles, but Article is the safe
//                          umbrella the rich-results test prefers)
//   - Person          (ContributorPage — extends the masthead Person
//                          with description, jobTitle, address, knowsAbout)
//   - BreadcrumbList  (all detail pages)
//
// Each helper returns a stringified JSON-LD payload ready to drop into
// `<script type="application/ld+json" dangerouslySetInnerHTML={...}>`.
//
// Why string and not object: matches the existing FAQPage helper in
// components/stateFaqs.js. Consistency over micro-optimization.

const SITE_URL = 'https://windowwashingguide.com';
const ORG_ID = `${SITE_URL}/#organization`;

function abs(path) {
  if (!path) return SITE_URL;
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

// ─── Article ─────────────────────────────────────────────────────────
// For /articles/<slug>. Uses Article (not NewsArticle, not Report) —
// the most permissive type that still wins rich-results enhancements.
//
// fm: article frontmatter; mustache fields used:
//   title, subtitle, slug, publishedDate, updatedDate, wordCount,
//   author.name, author.slug (resolved upstream via matchContributorName)
export function articleSchema(fm, authorSlug) {
  if (!fm || !fm.title) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: fm.title,
    description: fm.metaDescription || fm.subtitle || '',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': abs(`/articles/${fm.slug}`),
    },
    datePublished: fm.publishedDate || undefined,
    dateModified: fm.updatedDate || fm.publishedDate || undefined,
    wordCount: fm.wordCount || undefined,
    image: fm.image || undefined,           // s80: absolute hero URL when the article has one
    keywords: Array.isArray(fm.keywords) ? fm.keywords.join(', ') : undefined,
    articleSection: fm.pillarLabel || undefined,
    author: fm.author?.name
      ? {
          '@type': 'Person',
          name: fm.author.name,
          '@id': authorSlug ? abs(`/contributors/${authorSlug}#person`) : undefined,
          url: authorSlug ? abs(`/contributors/${authorSlug}`) : undefined,
        }
      : undefined,
    publisher: { '@id': ORG_ID },
    inLanguage: 'en-US',
  };
  return JSON.stringify(schema);
}

// ─── BlogPosting ─────────────────────────────────────────────────────
// For /blog/<slug>. BlogPosting (not Article) signals the right
// content-freshness expectations to crawlers.
//
// s69 — accepts an optional `authorContrib` argument. When provided
// and `authorContrib.entityType === 'Organization'`, the author block
// is emitted as Organization (with @id pointing at the org's
// contributor-page fragment). Without it, the original Person path is
// preserved exactly — so existing calls keep working unchanged.
export function blogPostingSchema(fm, authorSlug, authorContrib) {
  if (!fm || !fm.title) return null;
  const isOrgAuthor = authorContrib && authorContrib.entityType === 'Organization';
  const authorBlock = fm.author?.name
    ? (isOrgAuthor
      ? {
          '@type': 'Organization',
          name: fm.author.name,
          '@id': authorSlug ? abs(`/contributors/${authorSlug}#organization`) : undefined,
          url: authorSlug ? abs(`/contributors/${authorSlug}`) : undefined,
        }
      : {
          '@type': 'Person',
          name: fm.author.name,
          '@id': authorSlug ? abs(`/contributors/${authorSlug}#person`) : undefined,
          url: authorSlug ? abs(`/contributors/${authorSlug}`) : undefined,
        })
    : undefined;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: fm.title,
    description: fm.metaDescription || fm.excerpt || '',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': abs(`/blog/${fm.slug}`),
    },
    datePublished: fm.publishedDate || undefined,
    dateModified: fm.updatedDate || fm.publishedDate || undefined,
    wordCount: fm.wordCount || undefined,
    keywords: Array.isArray(fm.tags) ? fm.tags.join(', ') : undefined,
    articleSection: fm.category || undefined,
    image: fm.image || undefined,           // s139: absolute hero URL when the post has one (blog-images.json)
    author: authorBlock,
    publisher: { '@id': ORG_ID },
    inLanguage: 'en-US',
  };
  return JSON.stringify(schema);
}

// ─── Guide (TechArticle) ─────────────────────────────────────────────
// For /tools/<slug>/guide. Methodology guides are technical reference
// articles; TechArticle is the right fit, with Article as parent type
// implicit via @type singular convention.
export function guideSchema(fm, authorSlug) {
  if (!fm || !fm.title) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: fm.title,
    description: fm.metaDescription || fm.subtitle || '',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': abs(`/tools/${fm.toolSlug || fm.slug}/guide`),
    },
    datePublished: fm.publishedDate || undefined,
    dateModified: fm.updatedDate || fm.publishedDate || undefined,
    wordCount: fm.wordCount || undefined,
    proficiencyLevel: 'Expert',
    author: fm.author?.name
      ? {
          '@type': 'Person',
          name: fm.author.name,
          '@id': authorSlug ? abs(`/contributors/${authorSlug}#person`) : undefined,
          url: authorSlug ? abs(`/contributors/${authorSlug}`) : undefined,
        }
      : undefined,
    publisher: { '@id': ORG_ID },
    image: fm.image || undefined,           // s139: absolute hero URL when the guide has one (guide-images.json)
    inLanguage: 'en-US',
    about: fm.toolName
      ? { '@type': 'SoftwareApplication', name: fm.toolName, url: abs(fm.toolPath || `/tools/${fm.toolSlug || fm.slug}`) }
      : undefined,
  };
  return JSON.stringify(schema);
}

// ─── Person (contributor) ────────────────────────────────────────────
// For /contributors/<slug>. Extends the lightweight Person stub in
// index.html with the long-form description, jobTitle, location, and
// knowsAbout (their beats). The @id matches the masthead entry so the
// two records identify the same person to crawlers.
//
// s69 — branches on contributor.entityType. The Wire's News Desk
// contributor record carries entityType: 'Organization' and renders
// as an Organization rather than a Person. All other fields map
// straight across (Organization supports knowsAbout, jobTitle is not
// in Organization but description carries the same intent, and the
// location resolves to a Place rather than a PostalAddress so the
// Organization shape is canonical).
export function personSchema(contributor) {
  if (!contributor) return null;
  const slug = contributor.slug;
  const isOrg = contributor.entityType === 'Organization';

  if (isOrg) {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': abs(`/contributors/${slug}#organization`),
      name: contributor.name,
      url: abs(`/contributors/${slug}`),
      description: contributor.shortBio,
      knowsAbout: Array.isArray(contributor.beats) ? contributor.beats : undefined,
      parentOrganization: { '@id': ORG_ID },
    };
    return JSON.stringify(schema);
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': abs(`/contributors/${slug}#person`),
    name: contributor.name,
    url: abs(`/contributors/${slug}`),
    jobTitle: contributor.fullTitle,
    description: contributor.shortBio,
    knowsAbout: Array.isArray(contributor.beats)
      ? contributor.beats
      : undefined,
    worksFor: { '@id': ORG_ID },
    address: contributor.location
      ? {
          '@type': 'PostalAddress',
          addressLocality: contributor.location,
        }
      : undefined,
  };
  return JSON.stringify(schema);
}

// ─── ProfilePage (for contributor pages) ─────────────────────────────
// s69 — the spec for the News Desk author page asks for ProfilePage +
// Organization markup. The Organization payload is already emitted by
// personSchema() above when entityType is 'Organization'; this helper
// emits the ProfilePage wrapper that references the main entity.
//
// Used on /contributors/wwg-news-desk specifically, but written
// generically so it also applies if we later upgrade Person contributor
// pages to ProfilePage markup.
export function profilePageSchema(contributor) {
  if (!contributor) return null;
  const slug = contributor.slug;
  const isOrg = contributor.entityType === 'Organization';
  const fragment = isOrg ? 'organization' : 'person';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': abs(`/contributors/${slug}#profilepage`),
    mainEntity: { '@id': abs(`/contributors/${slug}#${fragment}`) },
    name: contributor.name,
    url: abs(`/contributors/${slug}`),
    description: contributor.shortBio,
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
  };
  return JSON.stringify(schema);
}

// ─── HowTo ────────────────────────────────────────────────────────────
// For procedural articles where the content genuinely describes an
// ordered sequence of steps a reader can execute. Emitted opt-in via
// frontmatter `howToSteps:` array; never inferred automatically.
//
// Frontmatter contract (top-level keys, matches the parser's array-of-
// objects pattern already used by embeddedTools and relatedArticles):
//   howToName: "How to wash a window properly"     # optional, defaults to title
//   howToTotalTime: "PT20M"                        # ISO 8601 duration, optional
//   howToSteps:
//     - name: "Apply"
//       text: "Apply solution onto the glass with a sleeve or sea sponge."
//     - name: "Squeegee"
//       text: "Remove the solution in one pattern (fan, straight pull, or S-stroke)."
//
// Why opt-in: Google's quality guidelines treat HowTo schema as a claim
// the page is a literal procedural walkthrough, not an editorial essay
// that happens to discuss a procedure. Most of the corpus is the
// latter. Article 02 (the four-stage wash) is the canonical case where
// the claim is true. Future how-to-shaped articles can opt in by
// adding the frontmatter block.
export function howToSchema(fm) {
  if (!fm || !Array.isArray(fm.howToSteps) || fm.howToSteps.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: fm.howToName || fm.title,
    description: fm.metaDescription || fm.subtitle || '',
    inLanguage: 'en-US',
    totalTime: fm.howToTotalTime || undefined,
    step: fm.howToSteps.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.name || `Step ${idx + 1}`,
      text: s.text || s.name || '',
      url: abs(`/articles/${fm.slug}#step-${idx + 1}`),
    })),
  };
  return JSON.stringify(schema);
}

// ─── WebApplication (tool pages) — s80, audit D6 ──────────────────────
// Every /tools/<slug> route is an interactive browser tool. Google's
// SoftwareApplication family is the type that matches; WebApplication is
// the subtype for something that runs in the browser with no install.
// Built from the same title/description/path the <head> already uses, so
// the schema can never disagree with the tags beside it.
export function webApplicationSchema({ name, description, path }) {
  if (!name || !path) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    '@id': abs(`${path}#webapp`),
    name,
    description: description || undefined,
    url: abs(path),
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': `${SITE_URL}/#website` },
  };
  return JSON.stringify(schema);
}

// ─── CollectionPage (hubs / indexes) — s80, audit D6 ──────────────────
// /articles, /blog, /states, /contributors, the four pillar indexes, etc.
// A hub is a page whose main content is a list of other pages. Kept
// deliberately thin: the value is telling Google "this is a hub, not a
// thin article", not enumerating every child (the links do that).
export function collectionPageSchema({ name, description, path }) {
  if (!name || !path) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': abs(`${path}#collection`),
    name,
    description: description || undefined,
    url: abs(path),
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    publisher: { '@id': ORG_ID },
  };
  return JSON.stringify(schema);
}

// ─── WebPage (plain static pages) — s80 ───────────────────────────────
// /contact, /sources, /editorial-standards, /submit, /find-a-cleaner.
// Not a hub and not an article; a typed WebPage with a breadcrumb is the
// honest minimum and keeps "every sitemap URL emits a type" a true gate.
export function webPageSchema({ name, description, path }) {
  if (!name || !path) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': abs(`${path}#webpage`),
    name,
    description: description || undefined,
    url: abs(path),
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    publisher: { '@id': ORG_ID },
  };
  return JSON.stringify(schema);
}

// ─── BreadcrumbList ──────────────────────────────────────────────────
// items: [{ name, path }] in order from root → current page.
// The current page should be the last item. Schema.org expects
// position to be 1-indexed.
export function breadcrumbSchema(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const list = items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: abs(it.path),
  }));
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list,
  };
  return JSON.stringify(schema);
}

// ─── Combined helper ──────────────────────────────────────────────────
// Pages that emit multiple schema types render each in its own
// <script type="application/ld+json"> block, but it's common to want
// to chain article + breadcrumb in one render path. Returns an array
// of stringified JSON-LD blocks for easy mapping.
export function combineSchemas(...blocks) {
  return blocks.filter(Boolean);
}
